/**
 * The Action Plan's modal dialog contract (S4h).
 *
 * Configure Costs and Regenerate were bare `div` overlays: no dialog role, no
 * aria-modal, no focus management, no Escape — a keyboard or screen-reader
 * user could neither reliably enter them nor leave them. This wrapper owns the
 * contract once, for both:
 *
 * - `role="dialog"` + `aria-modal="true"`, named by `label` and described by
 *   `description` (rendered, quietly — the description states what closing
 *   does, because CLOSING NEVER SUBMITS: Escape, the overlay, and any close
 *   control call `onClose` only, which hides the dialog and changes nothing).
 * - Focus ENTERS the dialog on open (its first focusable control), is TRAPPED
 *   inside (Tab and Shift+Tab wrap), and RETURNS to the element that invoked
 *   the dialog when it closes.
 * - Escape closes. A pointer-down on the overlay backdrop (not on dialog
 *   content) closes — Cancel semantics.
 *
 * The children keep their own visible headings and Save/Cancel/Close
 * controls; the wrapper supplies semantics, focus, and keys.
 */

import React, { useEffect, useId, useRef } from 'react';

interface ActionPlanModalProps {
  /** Accessible name for the dialog, e.g. "Regenerate Action Plan". */
  label: string;
  /**
   * One sentence of context, rendered inside the dialog and wired via
   * aria-describedby. Say what closing does — for these dialogs, nothing.
   */
  description: string;
  /** Class for the dialog box itself (e.g. regenerate-config-modal). */
  className: string;
  /** Hide the dialog. Must be state-only: closing never submits. */
  onClose: () => void;
  children: React.ReactNode;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const ActionPlanModal: React.FC<ActionPlanModalProps> = ({
  label,
  description,
  className,
  onClose,
  children,
}) => {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const invokerRef = useRef<HTMLElement | null>(null);
  const descriptionId = useId();

  /*
   * Capture the invoker at mount and return focus to it at unmount. Captured
   * BEFORE focus moves into the dialog, so it is the button that opened us,
   * not the dialog's own first control.
   */
  useEffect(() => {
    invokerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const first = dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();
    const invoker = invokerRef.current;
    return () => {
      invoker?.focus();
    };
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusables = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [],
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === dialogRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className="cost-config-overlay"
      onMouseDown={(event) => {
        // Backdrop only — a press that starts inside the dialog never closes it.
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        aria-describedby={descriptionId}
        className={className}
        onKeyDown={handleKeyDown}
      >
        <p id={descriptionId} className="ap-modal__description">
          {description}
        </p>
        {children}
      </div>
    </div>
  );
};
